import {
  AlertsResponseType,
  HoldingsDataType,
  MetaDataResponse,
  PeerResponse,
  RollingReturnsPerformanceData,
  RollingsRanksData,
  TrailingRanksData,
  TrailingReturnsPerformanceData,
  EliteScoresTypes,
  ElitePowerRankLeaderboardResponseType,
  RegularVsDirectDataResponseType,
  KnowWhyAPIReponseType
} from '../types/mutual-fund-detail.types'
import { InvestmentDataType } from '../features/user-investment/types/investmentApi.types'
import axiosInstance from '../utils/axiosConfig'
import { mobileBackendUrl } from '../env'

export const fetchMutualFundData = async (isin: string, from: string) => {
  return axiosInstance.get<MetaDataResponse>(
    `${mobileBackendUrl}/v1/mutual-fund/meta-data/${isin}${from ? `?requester=${from}` : ''}`
  )
}

export const fetchRollingReturnsPerformanceData = async ({
  isin,
  benchmarkType,
  benchmarkValue
}: {
  isin: string
  benchmarkType: string
  benchmarkValue: string
}) => {
  const query = new URLSearchParams({
    isin,
    benchmarkType,
    benchmarkValue
  })
  return axiosInstance.get<RollingReturnsPerformanceData>(
    `${mobileBackendUrl}/v1/mutual-fund/performance-graph/long-term-returns?${query.toString()}`
  )
}

export const fetchTrailingReturnsPerformanceData = async ({
  isin,
  benchmarkType,
  benchmarkValue,
  tenureValue,
  tenurePeriod,
  principal = 10000
}: {
  isin: string
  benchmarkType: string
  benchmarkValue: string
  tenureValue: number
  tenurePeriod: string
  principal?: number
}) => {
  const query = new URLSearchParams({
    benchmarkValue,
    benchmarkType,
    tenureValue: String(tenureValue),
    tenurePeriod,
    principal: String(principal)
  })
  return axiosInstance.get<TrailingReturnsPerformanceData>(
    `${mobileBackendUrl}/v1/mutual-fund/recent-returns/${isin}?${query.toString()}`
  )
}

export const fetchTrailingPeerData = async (category: string) => {
  return axiosInstance.get<PeerResponse>(`${mobileBackendUrl}/v1/mutual-fund/trailing-returns/${category}/peers-rank`)
}

export const fetchRollingPeerData = async (category: string) => {
  return axiosInstance.get<PeerResponse>(`${mobileBackendUrl}/v1/mutual-fund/rolling-returns/peers-rank/${category}`)
}

export const fetchRollingRankData = async (isin: string) => {
  return axiosInstance.get<RollingsRanksData>(`${mobileBackendUrl}/v1/mutual-fund/rolling-returns/${isin}/rank`)
}

export const fetchTrailingRankData = async (isin: string) => {
  return axiosInstance.get<TrailingRanksData>(`${mobileBackendUrl}/v1/mutual-fund/trailing-returns/rank/${isin}`)
}

export const fetchHoldingsData = async (isin: string) => {
  return axiosInstance.get<HoldingsDataType>(`${mobileBackendUrl}/v1/mutual-fund/holdings/${isin}`)
}

export const fetchInvestmentsData = async (isin: string) => {
  return axiosInstance.get<InvestmentDataType>(`${mobileBackendUrl}/v1/portfolio/user/${isin}/investment-data`)
}

export const fetchRegularVsDirectData = async (isin: string) => {
  return axiosInstance.get<RegularVsDirectDataResponseType>(
    `${mobileBackendUrl}/v2/portfolio/regular-vs-direct?isins=${isin}`
  )
}

export const fetchPowerPerformanceRank = async (isin: string): Promise<MetaDataResponse> => {
  return axiosInstance.get(`${mobileBackendUrl}/v1/mutual-fund/power-performance/${isin}/rank`)
}

export const fetchAlerts = async (isin: string, purchaseMode: string): Promise<AlertsResponseType> => {
  return axiosInstance.get(`${mobileBackendUrl}/v1/mutual-fund/alerts/${isin}?purchaseMode=${purchaseMode}`)
}

export const fetchEliteScores = async (isin: string, category: string): Promise<any> => {
  return axiosInstance.get(`${mobileBackendUrl}/v1/mutual-fund/return-score/isin/${isin}?category=${category}`)
}

export const fetchEliteScoreLeaderboard = async (scoreType: `${EliteScoresTypes}`, category: string): Promise<any> => {
  let backScoreType: 'STABILITY' | 'RETURNS' | 'RECENCY' = 'RETURNS'
  if (scoreType === EliteScoresTypes.CONSISTENCY) {
    backScoreType = 'RETURNS'
  } else if (scoreType === EliteScoresTypes.VOLATILITY) {
    backScoreType = 'STABILITY'
  } else if (scoreType === EliteScoresTypes.RECENCY) {
    backScoreType = 'RECENCY'
  }
  return axiosInstance.get(
    `${mobileBackendUrl}/v1/mutual-fund/categories/${category}/power-scores/${backScoreType}/peers`
  )
}

export const fetchPowerRankLeaderboard = async (category: string): Promise<ElitePowerRankLeaderboardResponseType> => {
  return axiosInstance.get(`${mobileBackendUrl}/v1/mutual-fund/categories/${category}/power-performance/peers`)
}

export const fetchESignData = async (): Promise<any> => {
  return axiosInstance.post(`${mobileBackendUrl}/v1/onboarding/e-sign/submit`, {
    url: window.location.href
  })
}

export const updateKycStatusAndGetAppCTA = () => {
  return axiosInstance.post(`${mobileBackendUrl}/v2/onboarding/web/digilocker-response`, {
    url: window.location.href
  })
}

export const fetchKnowWhyData = async (isin: string): Promise<{ data: KnowWhyAPIReponseType }> => {
  return axiosInstance.get(`${mobileBackendUrl}/v2/portfolio/know-why/${isin}`)
}
